// ABOUTME: Achievement manager service for automatic unlocking
// ABOUTME: Monitors user stats and unlocks achievements when criteria met

import type {
  IAchievementManager,
  UserAchievementStats,
  AchievementProgress,
} from '@/types/services'
import type { Achievement } from '@/types/content'
import { ACHIEVEMENTS, checkAchievementUnlock, getNewlyUnlockedAchievements } from '@/content/achievements'
import { useGameStore } from '@/lib/store/gameStore'
import { useModuleStore } from '@/lib/store/moduleStore'
import { useWardrobeStore } from '@/lib/store/wardrobeStore'
import { toast } from 'sonner'

/**
 * Achievement manager implementation
 * Automatically checks and unlocks achievements
 */
export class AchievementManager implements IAchievementManager {
  private unlockedAchievements: Set<string> = new Set()

  constructor() {
    this.loadUnlocked()
    this.setupAutoCheck()
  }

  /**
   * Load unlocked achievements from game store
   */
  private loadUnlocked(): void {
    const gameState = useGameStore.getState()
    this.unlockedAchievements = new Set(gameState.unlockedAchievements)
  }

  /**
   * Setup automatic achievement checking
   */
  private setupAutoCheck(): void {
    // Subscribe to game store changes
    useGameStore.subscribe((state, prevState) => {
      // Check if any relevant stats changed
      const statsChanged =
        state.level !== prevState.level ||
        state.experiencePoints !== prevState.experiencePoints ||
        state.totalChallengesCompleted !== prevState.totalChallengesCompleted

      if (statsChanged) {
        this.checkAndUnlockAll().catch(console.error)
      }
    })

    // Subscribe to module store changes
    useModuleStore.subscribe((state, prevState) => {
      if (state.completedModules.length !== prevState.completedModules.length) {
        this.checkAndUnlockAll().catch(console.error)
      }
    })

    // Subscribe to wardrobe store changes
    useWardrobeStore.subscribe((state, prevState) => {
      if (state.items.length !== prevState.items.length || state.outfits.length !== prevState.outfits.length) {
        this.checkAndUnlockAll().catch(console.error)
      }
    })

    console.log('✅ Achievement auto-check initialized')
  }

  /**
   * Check and unlock achievements based on user stats
   */
  async checkAchievements(userStats: UserAchievementStats): Promise<string[]> {
    const newlyUnlocked: string[] = []

    for (const achievement of ACHIEVEMENTS) {
      // Skip if already unlocked
      if (this.unlockedAchievements.has(achievement.id)) {
        continue
      }

      // Check if criteria met
      const unlocked = checkAchievementUnlock(achievement, userStats)

      if (unlocked) {
        await this.unlockAchievement(achievement.id)
        newlyUnlocked.push(achievement.id)
      }
    }

    return newlyUnlocked
  }

  /**
   * Unlock a specific achievement
   */
  async unlockAchievement(achievementId: string): Promise<void> {
    // Find achievement
    const achievement = ACHIEVEMENTS.find((a) => a.id === achievementId)
    if (!achievement) {
      throw new Error(`Achievement not found: ${achievementId}`)
    }

    // Check if already unlocked
    if (this.unlockedAchievements.has(achievementId)) {
      return
    }

    // Mark as unlocked
    this.unlockedAchievements.add(achievementId)

    // Update game store
    const gameStore = useGameStore.getState()
    gameStore.unlockAchievement(achievementId)

    // Award XP
    gameStore.addExperience(achievement.xpReward)

    // Show toast notification
    this.showAchievementToast(achievement)

    console.log(`🏆 Achievement unlocked: ${achievement.title} (+${achievement.xpReward} XP)`)
  }

  /**
   * Get all unlocked achievements
   */
  async getUnlockedAchievements(): Promise<string[]> {
    return Array.from(this.unlockedAchievements)
  }

  /**
   * Get progress towards specific achievement
   */
  async getAchievementProgress(achievementId: string): Promise<AchievementProgress> {
    const achievement = ACHIEVEMENTS.find((a) => a.id === achievementId)
    if (!achievement) {
      throw new Error(`Achievement not found: ${achievementId}`)
    }

    const unlocked = this.unlockedAchievements.has(achievementId)

    // Get current user stats
    const gameState = useGameStore.getState()
    const userStats: UserAchievementStats = {
      level: gameState.level,
      totalXP: gameState.experiencePoints,
      challengesCompleted: gameState.totalChallengesCompleted,
      modulesCompleted: gameState.modulesCompleted,
      itemsScanned: gameState.itemsScanned,
      streakDays: gameState.currentStreak,
      perfectQuizzes: gameState.perfectQuizzes,
      outfitsCreated: gameState.outfitsCreated,
    }

    // Calculate current value based on criteria type
    let current = 0
    const { criteria } = achievement

    switch (criteria.type) {
      case 'level_reached':
        current = userStats.level
        break
      case 'xp_earned':
        current = userStats.totalXP
        break
      case 'challenges_completed':
        current = userStats.challengesCompleted
        break
      case 'modules_completed':
        current = userStats.modulesCompleted
        break
      case 'items_scanned':
        current = userStats.itemsScanned
        break
      case 'streak_days':
        current = userStats.streakDays
        break
      case 'quizzes_perfect':
        current = userStats.perfectQuizzes
        break
      default:
        current = unlocked ? criteria.target : 0
    }

    const percentage = Math.min(100, Math.floor((current / criteria.target) * 100))

    return {
      achievementId,
      current,
      target: criteria.target,
      percentage,
      unlocked,
    }
  }

  /**
   * Check all achievements and unlock any that are newly unlocked
   */
  private async checkAndUnlockAll(): Promise<void> {
    const gameState = useGameStore.getState()

    const userStats: UserAchievementStats = {
      level: gameState.level,
      totalXP: gameState.experiencePoints,
      challengesCompleted: gameState.totalChallengesCompleted,
      modulesCompleted: gameState.modulesCompleted,
      itemsScanned: gameState.itemsScanned,
      streakDays: gameState.currentStreak,
      perfectQuizzes: gameState.perfectQuizzes,
      outfitsCreated: gameState.outfitsCreated,
    }

    const currentUnlocked = Array.from(this.unlockedAchievements)
    const newUnlocks = getNewlyUnlockedAchievements(currentUnlocked, userStats)

    for (const achievement of newUnlocks) {
      await this.unlockAchievement(achievement.id)
    }
  }

  /**
   * Show toast notification for achievement unlock
   */
  private showAchievementToast(achievement: Achievement): void {
    const rarityEmoji: Record<Achievement['rarity'], string> = {
      common: '⭐',
      rare: '💫',
      epic: '✨',
      legendary: '🏆',
    }

    toast.success(`${rarityEmoji[achievement.rarity]} Achievement Unlocked!`, {
      description: `${achievement.title} - ${achievement.description}`,
      duration: 5000,
    })
  }

  /**
   * Reset all achievements (for testing)
   */
  reset(): void {
    this.unlockedAchievements.clear()
  }
}

// Singleton instance
let managerInstance: AchievementManager | null = null

/**
 * Get achievement manager instance
 */
export function getAchievementManager(): AchievementManager {
  if (!managerInstance) {
    managerInstance = new AchievementManager()
  }
  return managerInstance
}

/**
 * Initialize achievement system
 */
export function setupAchievementSystem(): void {
  getAchievementManager()
  console.log('✅ Achievement system initialized')
}
